import { all, call, fork, put, select, takeEvery } from "redux-saga/effects";
import {
  SALVA_USUARIO_PORTAL, INICIA_USUARIO_PORTAL,
  BUSCA_PESSOA_BASE_UZER_USUARIO_PORTAL,
  BUSCA_USUARIO_PORTAL_EDICAO,
  REMOVE_USUARIO_PORTAL,
  LISTA_PERFIS_POR_CLIENTE,
  LISTA_USUARIO_PORTAL
} from "constants/ActionTypes";
import requisicaoApi from 'util/Api';
import requisicaoApiSeguranca from 'util/ApiSeguranca';
import { userSignOut } from "actions/Auth";
import {
  showAuthMessage,
  exibirMensagemErro,
  salvaUsuarioPortalSucesso,
  removeUsuarioPortalSucesso,
  inicioUsuarioPortalSucesso,
  iniciaUsuarioPortal,
  buscaUsuarioPortalEdicaoSucesso,
  listaPerfisSucesso,
  listaUsuarioPortalSucesso,
  abortaOperacao
} from "actions/UsuarioPortal";
import { limpaValidacoes } from "actions/Global";
import md5 from "md5";
import moment from "moment";

/*#################################################################################################################*/
/* Todos os endpoints de identidade/pessoa/permissões desta feature agora são da
   Uzerpass.Seguranca. A pessoa física (CPF, endereço, contatos, foto) é lida/gravada via
   Dapper direto no banco legado (dentro da própria API nova); o legado só continua sendo
   chamado para o catálogo de ambientes/pontos de acesso da Portaria, que nunca foi migrado
   pra lá (são ids numéricos externos, ver docs/dominio-e-modelo.md). Ver
   docs/plano-integracao-api-seguranca.md.

   Modelo de permissão mudou: o legado era "perfil OU configuração customizada" (exclusivo,
   com um id=0 "Customizado"); a Uzerpass.Seguranca é aditiva — perfis e permissões diretas
   (funcionalidadeAcaoIds) SE SOMAM. Por isso perfilIdsSelecionados é uma lista (multi-seleção)
   e a matriz "Administrativo" fica sempre editável, não só quando "Customizado" está ativo.

   "Bloco de funcionalidade" não existe mais na API nova — o agrupamento visual é
   reconstruído aqui a partir da árvore módulo → item de menu (Funcionalidade.itemMenuId),
   agrupando cada funcionalidade sob o item de menu de nível mais alto (a raiz da sua
   subárvore), que faz as vezes do antigo "bloco". */
/*#################################################################################################################*/

const SIM = 'S';

const somenteDigitos = (valor) => (valor || '').replace(/\D/g, '');

const dataIsoOuNull = (dataStr) => {
  const data = moment(dataStr, 'DD/MM/YYYY', true);
  return data.isValid() ? data.format('YYYY-MM-DD') : null;
};

const mapeiaContatos = (lista, campoValor) =>
  (lista || [])
    .filter(item => item[campoValor])
    .map(item => ({ valor: item[campoValor], marcador: item.marcador }));

// O reducer (reducers/UsuarioPortal.js) e os componentes de _dadosPessoais/_endereco/_contato
// esperam o formato de campos do DTO antigo do legado (nomePessoa, numeroCpf, imagem,
// dataNascimentoStr, id numérico 0 = "novo", etc.) — em vez de reescrever esse reducer
// grande e arriscado, traduzimos aqui a resposta da Uzerpass.Seguranca pro mesmo formato.
// GUID vazio (pessoa existe mas ainda não é usuário do portal) vira id=0, preservando os
// checks "retorno.id !== 0"/"usuarioPortal.id === 0" que já existem no reducer/saga.
const GUID_VAZIO = '00000000-0000-0000-0000-000000000000';

const mapeiaParaFormulario = (dto, vinculo, ambientes, modulosComBlocos, funcionalidadesAcao) => {
  const endereco = dto.endereco || {};
  let idTemporarioTelefone = 0;
  let idTemporarioEmail = 0;
  const portaria = (vinculo && vinculo.portaria) || {};

  return {
    id: (!dto.id || dto.id === GUID_VAZIO) ? 0 : dto.id,
    nomePessoa: dto.nome,
    numeroCpf: dto.cpf,
    dataNascimentoStr: dto.dataNascimento ? moment(dto.dataNascimento).format('DD/MM/YYYY') : '',
    nomeMae: dto.nomeMae,
    sexo: dto.sexo,
    estadoCivil: dto.estadoCivil,
    imagem: dto.foto,
    cep: endereco.cep || '',
    logradouro: endereco.logradouro || '',
    numero: endereco.numero || '',
    complemento: endereco.complemento || '',
    semNumero: !!endereco.semNumero,
    bairro: endereco.bairro || '',
    uf: endereco.uf || '',
    nomeUf: endereco.uf || '',
    idMunicipio: 0,
    nomeMunicipio: endereco.nomeMunicipio || '',
    codigoIbge: endereco.codigoIbge || null,
    pontoReferencia: endereco.pontoReferencia || '',
    telefoneUm: '',
    telefoneDois: '',
    email: dto.email,
    site: '',
    login: dto.login || '',
    senha: '',
    senhaCript: '',
    idClienteFixado: 0,
    idPerfilDoUsuario: 0,
    perfisVinculados: [],
    clientesVinculados: [],
    telefones: (dto.telefones || []).map(t => ({ id: 0, idTemporario: idTemporarioTelefone++, numeroTelefone: t.valor, marcador: t.marcador || '', exclusivoDoCliente: false })),
    emails: (dto.emails || []).map(e => ({ id: 0, idTemporario: idTemporarioEmail++, email: e.valor, marcador: e.marcador || '', exclusivoDoCliente: false })),

    // Permissões (Uzerpass.Seguranca) do vínculo com o cliente ativo — vazio quando o usuário
    // ainda não tem vínculo com esse cliente (ex.: acabou de ser criado, ou não migrado).
    perfilIdsSelecionados: (vinculo && vinculo.perfis || []).map(p => p.id),
    listaConfiguracaoPortalFuncionalideAcao: (vinculo && vinculo.funcionalidadeAcaoIds || []).map(id => ({ idFuncionalidadeAcaoUsuarioPortal: id })),
    listaPontosAcessoPortaria: construirListaPontosAcessoPortaria(portaria.pontosDeAcessoIds, ambientes),
    // Precisa levar as listas de apoio (ambientes/blocos/funcionalidades) junto: o reducer usa
    // ESTE objeto inteiro pra usuário já existente (BUSCA_USUARIO_PORTAL_EDICAO_SUCESSO), em
    // vez de reconstruir a partir do bootstrap como faz pra usuário novo.
    configuracaoAcessoPortalUsuario: {
      visualizaDadosPortaria: !!portaria.visualizaDadosPortaria,
      concedeAcessos: !!portaria.concedeAcessos,
      usuarioMantemGaragem: !!portaria.usuarioMantemGaragem,
      listaDeAmbientesPortariaDoCliente: ambientes || [],
      listaModulosComBlocos: modulosComBlocos || [],
      listaFuncionalidadesAcao: funcionalidadesAcao || [],
    },
  };
};

const mapeiaParaListagem = (dto) => ({
  id: dto.id,
  imagem: dto.foto,
  nomePessoa: dto.nome,
  numeroCpf: dto.cpf,
  sexo: dto.sexo,
  dataNascimento: dto.dataNascimento,
  login: dto.login,
});

const mapeiaEndereco = (usuarioPortal) => {
  if (!usuarioPortal.cep && !usuarioPortal.logradouro) return null;

  return {
    cep: somenteDigitos(usuarioPortal.cep),
    logradouro: usuarioPortal.logradouro,
    numero: usuarioPortal.numero,
    semNumero: !!usuarioPortal.semNumero,
    complemento: usuarioPortal.complemento,
    pontoReferencia: usuarioPortal.pontoReferencia,
    bairro: usuarioPortal.bairro,
    nomeMunicipio: usuarioPortal.nomeMunicipio,
    uf: usuarioPortal.uf,
    codigoIbge: usuarioPortal.codigoIbge ? Number(usuarioPortal.codigoIbge) : 0,
  };
};

// Achata a árvore de itens de menu (módulo → item → subitens) num mapa id -> nó, preservando
// itemPaiId (pra subir até a raiz, o "bloco") e o módulo dono (pra agrupar os blocos por
// módulo — Backoffice/Comunicação/Validação de Perfil não devem aparecer misturados).
const achatarArvoreItensMenu = (itens, acc, moduloId, moduloNome, moduloOrdem) => {
  (itens || []).forEach(item => {
    acc[item.id] = { id: item.id, nome: item.nome, itemPaiId: item.itemPaiId, ordem: item.ordem, moduloId, moduloNome, moduloOrdem };
    achatarArvoreItensMenu(item.itens, acc, moduloId, moduloNome, moduloOrdem);
  });
};

const encontrarRaizDoBloco = (itensPorId, itemMenuId) => {
  let atual = itensPorId[itemMenuId];
  if (!atual) return null;
  while (atual.itemPaiId && itensPorId[atual.itemPaiId]) {
    atual = itensPorId[atual.itemPaiId];
  }
  return atual;
};

// Substitui o antigo /util/blocosFuncionalidade + /util/funcionalidadesAcao (legado): a
// Uzerpass.Seguranca não agrupa funcionalidade por bloco, então reconstruímos aqui a partir
// da árvore de menu, agrupando os blocos dentro do módulo dono (Backoffice, Comunicação,
// Validação de Perfil — Portaria fica de fora, seus itens são livres e não têm
// funcionalidade). Devolve listaModulosComBlocos (pra _administrativo.js renderizar uma
// seção por módulo) e listaFuncionalidadesAcao (cada linha carrega
// funcionalidadePortal.idBlocoFuncionalidadePortal, exigido pelo reducer em
// SELECIONAR_TUDO_FUNCIONALIDADE — "Selecionar tudo" do bloco).
// todasFuncionalidadesAcao vem de GET /funcionalidades/acoes (uma chamada só, todo o
// catálogo) — evita fazer um GET /funcionalidades/{id} por funcionalidade.
const construirCatalogoPermissoes = (modulos, itensMenuPorModulo, funcionalidades, todasFuncionalidadesAcao) => {
  const itensPorId = {};
  (modulos || []).forEach(modulo => {
    achatarArvoreItensMenu(itensMenuPorModulo[modulo.id], itensPorId, modulo.id, modulo.nome, modulo.ordem);
  });

  const acoesPorFuncionalidade = {};
  (todasFuncionalidadesAcao || []).forEach(a => {
    if (!acoesPorFuncionalidade[a.funcionalidadeId]) acoesPorFuncionalidade[a.funcionalidadeId] = [];
    acoesPorFuncionalidade[a.funcionalidadeId].push(a);
  });

  const modulosPorId = {};
  const linhasFuncionalidadeAcao = [];

  (funcionalidades || []).forEach(f => {
    if (!f.itemMenuId || !itensPorId[f.itemMenuId]) return;

    const raiz = encontrarRaizDoBloco(itensPorId, f.itemMenuId);
    if (!raiz) return;

    if (!modulosPorId[raiz.moduloId]) {
      modulosPorId[raiz.moduloId] = { moduloId: raiz.moduloId, moduloNome: raiz.moduloNome, moduloOrdem: raiz.moduloOrdem, blocosPorId: {} };
    }
    const modulo = modulosPorId[raiz.moduloId];

    if (!modulo.blocosPorId[raiz.id]) {
      modulo.blocosPorId[raiz.id] = { id: raiz.id, nomeBloco: raiz.nome, ordemBloco: raiz.ordem, funcionalidadesDoBloco: [] };
    }

    modulo.blocosPorId[raiz.id].funcionalidadesDoBloco.push({
      id: f.id,
      nomeFuncionalidadePortal: f.nome,
      crudPadrao: f.crudPadrao,
      ordem: f.ordem,
    });

    (acoesPorFuncionalidade[f.id] || []).forEach(a => {
      linhasFuncionalidadeAcao.push({
        id: a.funcionalidadeAcaoId,
        idFuncionalidadePortal: f.id,
        acaoUsuarioPortal: { nomeAcaoUsuarioPortal: a.nome },
        funcionalidadePortal: { idFuncionalidadePortal: f.id, idBlocoFuncionalidadePortal: raiz.id },
      });
    });
  });

  const listaModulosComBlocos = Object.values(modulosPorId)
    .map(m => ({ moduloId: m.moduloId, moduloNome: m.moduloNome, moduloOrdem: m.moduloOrdem, blocos: Object.values(m.blocosPorId) }))
    .sort((a, b) => Number(a.moduloOrdem) - Number(b.moduloOrdem));

  return { listaModulosComBlocos, funcionalidadesAcao: linhasFuncionalidadeAcao };
};

// Reconstrói listaPontosAcessoPortaria (formato que _portaria.js já sabe renderizar) a partir
// dos ids liberados (long[], legado) devolvidos pela Uzerpass.Seguranca, cruzando com o
// catálogo de ambientes/pontos (também legado, único lugar que sabe o nome/ambiente de cada
// ponto — a Uzerpass.Seguranca só guarda o id, ver docs/dominio-e-modelo.md).
const construirListaPontosAcessoPortaria = (pontosLiberados, ambientes) =>
  (pontosLiberados || []).map(idPontoAcesso => {
    const ambiente = (ambientes || []).find(a => (a.pontosAcesso || []).some(p => p.id === idPontoAcesso));
    return { id: 0, idConfiguracaoAcessoPortal: 0, idPontoAcesso, pontoAcesso: { id: idPontoAcesso, idAmbiente: ambiente ? ambiente.id : null } };
  });

const corpoUsuarioPortal = (usuarioPortal) => ({
  nome: usuarioPortal.nomePessoa,
  cpf: somenteDigitos(usuarioPortal.numeroCpf),
  dataNascimento: dataIsoOuNull(usuarioPortal.dataNascimentoStr),
  nomeMae: usuarioPortal.nomeMae,
  sexo: usuarioPortal.sexo,
  estadoCivil: usuarioPortal.estadoCivil,
  foto: usuarioPortal.imagem,
  email: usuarioPortal.email,
  endereco: mapeiaEndereco(usuarioPortal),
  telefones: mapeiaContatos(usuarioPortal.telefones, 'numeroTelefone'),
  emails: mapeiaContatos(usuarioPortal.emails, 'email'),
});

/*#################################################################################################################*/
/* --------------------------------------------- Requests do tipo GET ---------------------------------------------*/

const getUsuariosPortal = async () =>
  await requisicaoApiSeguranca.get('/usuarios-portal')
    .then(resposta => resposta)
    .catch(error => error);

const getUsuarioPortalEdicao = async (id) =>
  await requisicaoApiSeguranca.get('/usuarios-portal/' + id)
    .then(resposta => resposta)
    .catch(error => error);

const getUsuarioPorCpf = async (cpf) =>
  await requisicaoApiSeguranca.get('/usuarios-portal/cpf/' + somenteDigitos(cpf))
    .then(resposta => resposta)
    .catch(error => error);

// Ambientes/pontos de acesso da Portaria: nunca existiu equivalente na Uzerpass.Seguranca (são
// ids numéricos externos que ela só guarda referência, ver docs/dominio-e-modelo.md) — continua
// vindo do legado, único lugar que sabe nome/agrupamento de cada ponto.
const getAmbientesPortaria = async () =>
  await requisicaoApi.get('/util/ambientesPortaria')
    .then(resposta => resposta)
    .catch(error => error);

const getPerfisSeguranca = async (clienteId) =>
  await requisicaoApiSeguranca.get('/perfis', { params: { clienteId, tamanhoPagina: 100 } })
    .then(resposta => resposta)
    .catch(error => error);

const getModulosSeguranca = async () =>
  await requisicaoApiSeguranca.get('/modulos')
    .then(resposta => resposta)
    .catch(error => error);

const getItensMenuSeguranca = async (moduloId) =>
  await requisicaoApiSeguranca.get(`/modulos/${moduloId}/itens-menu`)
    .then(resposta => resposta)
    .catch(error => error);

const getFuncionalidadesSeguranca = async () =>
  await requisicaoApiSeguranca.get('/funcionalidades', { params: { tamanhoPagina: 200 } })
    .then(resposta => resposta)
    .catch(error => error);

// Todo o catálogo de pares funcionalidade+ação numa chamada só — evita um
// GET /funcionalidades/{id} por funcionalidade (era o gargalo do carregamento da tela).
const getFuncionalidadesAcaoTodasSeguranca = async () =>
  await requisicaoApiSeguranca.get('/funcionalidades/acoes')
    .then(resposta => resposta)
    .catch(error => error);

const getUsuarioSegurancaPorId = async (id) =>
  await requisicaoApiSeguranca.get(`/usuarios/${id}`)
    .then(resposta => resposta)
    .catch(error => error);

const salvarVinculoClienteSeguranca = async (usuarioId, clienteId, corpo) =>
  await requisicaoApiSeguranca.put(`/usuarios/${usuarioId}/clientes/${clienteId}`, corpo)
    .then(resposta => resposta)
    .catch(error => error);

/*#################################################################################################################*/
/* ------------------------------------- Requests do tipo POST, PUT E DELETE --------------------------------------*/

const adicionaUsuarioPortalRequest = async (usuarioPortal, senhaMd5) =>
  await requisicaoApiSeguranca.post('/usuarios-portal', {
    ...corpoUsuarioPortal(usuarioPortal),
    login: usuarioPortal.login,
    senha: senhaMd5,
  })
    .then(resposta => resposta)
    .catch(error => error);

const atualizaUsuarioPortalRequest = async (usuarioPortal) =>
  await requisicaoApiSeguranca.put('/usuarios-portal', {
    ...corpoUsuarioPortal(usuarioPortal),
    id: usuarioPortal.id,
    login: usuarioPortal.login,
  })
    .then(resposta => resposta)
    .catch(error => error);

const deleteUsuarioPortalRequest = async (id) =>
  await requisicaoApiSeguranca.delete('/usuarios-portal/' + id)
    .then(resposta => resposta)
    .catch(error => error);

/*#################################################################################################################*/
/* --------------------------------------------------- Functions --------------------------------------------------*/
/*#################################################################################################################*/

// Busca o catálogo de permissões (perfis/módulos+menu/funcionalidades+ações) da
// Uzerpass.Seguranca. Roda uma vez no bootstrap da tela (mesmo padrão do legado, que também
// carregava tudo de uma vez em INICIA_USUARIO_PORTAL).
function* buscaCatalogoPermissoesSeguranca() {
  const clienteAtivo = yield select(state => state.seguranca && state.seguranca.clienteAtivo);
  const clienteId = clienteAtivo ? clienteAtivo.id : null;

  const perfis = yield call(getPerfisSeguranca, clienteId);
  const modulos = yield call(getModulosSeguranca);

  const itensMenuPorModulo = {};
  for (const modulo of (modulos.data || [])) {
    const itensMenu = yield call(getItensMenuSeguranca, modulo.id);
    itensMenuPorModulo[modulo.id] = itensMenu.data || [];
  }

  const funcionalidades = yield call(getFuncionalidadesSeguranca);
  const listaFuncionalidades = (funcionalidades.data && funcionalidades.data.data) || [];
  const todasFuncionalidadesAcao = yield call(getFuncionalidadesAcaoTodasSeguranca);
  const { listaModulosComBlocos, funcionalidadesAcao } = construirCatalogoPermissoes(
    modulos.data || [], itensMenuPorModulo, listaFuncionalidades, todasFuncionalidadesAcao.data || []
  );

  return {
    perfis: (perfis.data && perfis.data.data) || [],
    modulos: modulos.data || [],
    listaModulosComBlocos,
    funcionalidadesAcao,
  };
}

function* fnIniciaUsuarioPortal() {
  try {
    const listagemGrid = yield call(getUsuariosPortal);

    if (!listagemGrid.isAxiosError && listagemGrid.data) {
      const ambientesPortaria = yield call(getAmbientesPortaria);
      const catalogoPermissoes = yield call(buscaCatalogoPermissoesSeguranca);

      yield put(inicioUsuarioPortalSucesso(
        (listagemGrid.data || []).map(mapeiaParaListagem),
        [],
        catalogoPermissoes.perfis,
        ambientesPortaria.data || [],
        catalogoPermissoes.listaModulosComBlocos,
        catalogoPermissoes.funcionalidadesAcao,
        catalogoPermissoes.modulos,
      ));
      yield put(limpaValidacoes());
    }
    else if (listagemGrid.response && listagemGrid.response.status === 401) {
      yield put(userSignOut());
    }
    else {
      yield put(showAuthMessage('Ocorreu um erro ao listar usuariosPortal'));
    }
  } catch (error) {
    yield put(showAuthMessage(error));
  }
}

// Busca o vínculo (perfis/funcionalidadeAcaoIds/portaria) do usuário com o cliente ativo. null
// quando o usuário ainda não tem vínculo nenhum com esse cliente (ex.: acabou de ser criado).
function* buscaVinculoClienteAtivo(usuarioId) {
  const clienteAtivo = yield select(state => state.seguranca && state.seguranca.clienteAtivo);
  if (!clienteAtivo) return null;

  const retorno = yield call(getUsuarioSegurancaPorId, usuarioId);
  if (retorno.isAxiosError || !retorno.data) return null;

  return (retorno.data.clientes || []).find(c => c.clienteId === clienteAtivo.id) || null;
}

function* fnBuscaUsuarioPortalEdicao({ payload }) {
  try {
    const id = payload;
    const retorno = yield call(getUsuarioPortalEdicao, id);

    if (!retorno.isAxiosError && retorno.data) {
      const vinculo = yield call(buscaVinculoClienteAtivo, id);
      const ambientes = yield select(state => state.usuarioPortal.listaDeAmbientesPortariaDoClienteNovo);
      const modulosComBlocos = yield select(state => state.usuarioPortal.listaModulosComBlocosNovo);
      const funcionalidadesAcao = yield select(state => state.usuarioPortal.listaFuncionalidadesAcaoNovo);

      yield put(buscaUsuarioPortalEdicaoSucesso(mapeiaParaFormulario(retorno.data, vinculo, ambientes, modulosComBlocos, funcionalidadesAcao)));
      yield put(limpaValidacoes());
    }
    else {
      yield put(showAuthMessage('Ocorreu um erro ao retornar os dados do usuario: ' + id));
    }
  } catch (error) {
    yield put(showAuthMessage(error));
  }
}

function* fnBuscaPessoaBaseUzerUsuarioPortal({ payload }) {
  try {
    const cpf = payload;
    const usuario = yield call(getUsuarioPorCpf, cpf);

    if (!usuario.isAxiosError && usuario.data) {
      yield put(buscaUsuarioPortalEdicaoSucesso(mapeiaParaFormulario(usuario.data)));
      yield put(limpaValidacoes());
    }
    else if (!usuario.isAxiosError && usuario.status === 204) {
      yield put(abortaOperacao());
    }
    else {
      yield put(showAuthMessage('Ocorreu um erro ao retornar os dados do usuario: ' + cpf));
    }
  } catch (error) {
    yield put(showAuthMessage(error));
  }
}

function* fnListaPerfisPorCliente() {
  try {
    yield put(listaPerfisSucesso([]));
  } catch (error) {
    yield put(showAuthMessage(error));
  }
}

function* fnListaUsuarioPortal() {
  try {
    yield put(listaUsuarioPortalSucesso([]));
  } catch (error) {
    yield put(showAuthMessage(error));
  }
}

// Salva perfis + permissões diretas + portaria do usuário no cliente ativo — um PUT só,
// substitui tudo (ver SalvarVinculoClienteCommandHandler). Cria o vínculo com o cliente se
// ainda não existir. Melhor esforço: se o cliente ativo não estiver definido, não tem pra
// onde salvar permissão nenhuma — a identidade/pessoa já foi salva de qualquer forma.
function* salvaPermissoesUsuarioPortal(usuarioId, usuarioPortal) {
  const clienteAtivo = yield select(state => state.seguranca && state.seguranca.clienteAtivo);
  if (!clienteAtivo) return;

  const configuracao = usuarioPortal.configuracaoAcessoPortalUsuario || {};

  yield call(salvarVinculoClienteSeguranca, usuarioId, clienteAtivo.id, {
    perfilIds: usuarioPortal.perfilIdsSelecionados || [],
    funcionalidadeAcaoIds: (usuarioPortal.listaConfiguracaoPortalFuncionalideAcao || []).map(x => x.idFuncionalidadeAcaoUsuarioPortal),
    portaria: {
      visualizaDadosPortaria: !!configuracao.visualizaDadosPortaria,
      concedeAcessos: !!configuracao.concedeAcessos,
      usuarioMantemGaragem: !!configuracao.usuarioMantemGaragem,
      pontosDeAcessoIds: (usuarioPortal.listaPontosAcessoPortaria || []).map(x => x.idPontoAcesso),
    },
  });
}

function* fnSalvaUsuarioPortal({ payload }) {
  try {
    const usuarioPortal = payload;
    const criando = usuarioPortal.id === 0;

    let retorno;
    if (criando) {
      const senhaMd5 = md5(usuarioPortal.senha);
      retorno = yield call(adicionaUsuarioPortalRequest, usuarioPortal, senhaMd5);
    }
    else {
      retorno = yield call(atualizaUsuarioPortalRequest, usuarioPortal);
    }

    if (!retorno.isAxiosError && retorno.data) {
      const usuarioId = retorno.data.id;
      if (usuarioId) {
        yield call(salvaPermissoesUsuarioPortal, usuarioId, usuarioPortal);
      }
      yield put(iniciaUsuarioPortal());
      yield put(salvaUsuarioPortalSucesso(criando ? 'Inclusão realizada com sucesso.' : 'Alteração realizada com sucesso.'));
    }
    else {
      const notifications = retorno.response && retorno.response.data && retorno.response.data.notifications;
      const mensagem = notifications && notifications.length > 0
        ? notifications.map(n => n.message).join('; ')
        : 'Ocorreu um erro interno.';
      yield put(exibirMensagemErro(mensagem));
    }
  } catch (error) {
    yield put(showAuthMessage(error));
  }
}

function* fnRemoveUsuarioPortal({ payload }) {
  try {
    const id = payload;
    const retorno = yield call(deleteUsuarioPortalRequest, id);

    if (!retorno.isAxiosError) {
      yield put(iniciaUsuarioPortal());
      yield put(removeUsuarioPortalSucesso('Exclusão realizada com sucesso.'));
    }
    else {
      const notifications = retorno.response && retorno.response.data && retorno.response.data.notifications;
      const mensagem = notifications && notifications.length > 0
        ? notifications.map(n => n.message).join('; ')
        : 'Erro desconhecido';
      yield put(exibirMensagemErro(mensagem));
    }
  } catch (error) {
    yield put(showAuthMessage(error));
  }
}

/*#################################################################################################################*/
/* -------------------------------------------- Chamadas de Funções -----------------------------------------------*/
/*#################################################################################################################*/

export function* iniciaAcoesUsuarioPortal() {
  yield takeEvery(INICIA_USUARIO_PORTAL, fnIniciaUsuarioPortal);
}

export function* buscaPessoaBaseUzerUsuarioPortal() {
  yield takeEvery(BUSCA_PESSOA_BASE_UZER_USUARIO_PORTAL, fnBuscaPessoaBaseUzerUsuarioPortal)
}

export function* salvaUsuarioPortal() {
  yield takeEvery(SALVA_USUARIO_PORTAL, fnSalvaUsuarioPortal);
}

export function* buscaUsuarioPortalEdicao() {
  yield takeEvery(BUSCA_USUARIO_PORTAL_EDICAO, fnBuscaUsuarioPortalEdicao);
}

export function* removeUsuarioPortal() {
  yield takeEvery(REMOVE_USUARIO_PORTAL, fnRemoveUsuarioPortal);
}

export function* listaPerfisPorCliente() {
  yield takeEvery(LISTA_PERFIS_POR_CLIENTE, fnListaPerfisPorCliente);
}

export function* listaUsuarioPortal() {
  yield takeEvery(LISTA_USUARIO_PORTAL, fnListaUsuarioPortal)
}

/*#################################################################################################################*/
/* ------------------------------------------- Registro de Chamadas -----------------------------------------------*/
/*#################################################################################################################*/

export default function* rootSaga() {
  yield all([fork(iniciaAcoesUsuarioPortal),
  fork(buscaPessoaBaseUzerUsuarioPortal),
  fork(salvaUsuarioPortal),
  fork(buscaUsuarioPortalEdicao),
  fork(removeUsuarioPortal),
  fork(listaPerfisPorCliente),
  fork(listaUsuarioPortal)]);
}
